import { ChangeEvent, useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import type { RenderLayerProps } from 'react-laag';

import { searchInOptions } from 'utils/search';

import FormInput from '../../FormInput';
import { IInlineSelectProps, IOptions } from '../types';
import styles from './index.module.css';

type SearchableVariantProps = {
  minWidth: number;
  options: IInlineSelectProps['options'];
  onChange: IInlineSelectProps['onChange'];
  renderOption: IInlineSelectProps['renderOption'];
  selectedOption: IOptions | null;
  groups: IInlineSelectProps['groups'];
  groupBy: IInlineSelectProps['groupBy'];
  searchFn: IInlineSelectProps['searchFn'];
} & RenderLayerProps;

export function SearchableVariant(props: SearchableVariantProps) {
  const {
    isOpen,
    layerProps,
    options,
    minWidth,
    onChange,
    close,
    groupBy: getOptionGroups,
    groups: providedGroups,
    renderOption = (x) => x.label,
    selectedOption,
    searchFn = searchInOptions,
  } = props;

  const [search, setSearch] = useState('');

  const optionsRef = useRef<HTMLDivElement>(null);

  const scrollToActiveOption = () =>
    optionsRef.current?.querySelector('button[data-active]')?.scrollIntoView({ block: 'center' });

  useEffect(() => {
    if (isOpen) {
      scrollToActiveOption();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const groups =
    providedGroups &&
    options.reduce<{ [x: string]: Array<string | number> }>((acc, cur) => {
      if (!getOptionGroups) {
        const firstGroup = providedGroups[0].value!;
        return { ...acc, [providedGroups[0].value!]: [...(acc[firstGroup] || []), cur.value!] };
      }

      const groupsForOption = getOptionGroups(cur);

      const accCopy = { ...acc };

      if (Array.isArray(groupsForOption)) {
        groupsForOption.forEach((group) => {
          accCopy[group] = [...(acc[group] || []), cur.value!];
        });
      } else {
        const group = groupsForOption;
        accCopy[group] = [...(acc[group] || []), cur.value!];
      }

      return accCopy;
    }, {});

  const searched = providedGroups
    ? (Object.entries(groups!)
        .map(([group, values]) => {
          const optionsFromValues = options.filter((option) => values.includes(option.value!));
          return [group, searchFn(optionsFromValues, search)];
        })
        .filter(([, options]) => options.length > 0) as [string, IOptions[]][])
    : searchFn(options, search);

  const searchedGroups =
    providedGroups &&
    (searched as [string, IOptions[]][]).map(([group, options]) => ({
      group: providedGroups.find((g) => g.value === group)!,
      options,
    }));

  return (
    <div {...layerProps} className={styles.searchable} style={{ ...layerProps.style, minWidth }}>
      <svg
        className={styles.arrow}
        width="20"
        height="10"
        viewBox="0 0 20 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M19.5 10L10 1.07683e-06L0.5 10L19.5 10Z" fill="white" />
      </svg>

      <div className={styles.box}>
        <FormInput
          type="text"
          labelText=""
          fieldTipsRef={{ current: null }}
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          placeholder="Search currencies..."
          className={styles.input}
          autoFocus
        />
        <div className={styles.optionsContainer}>
          {/* TOFIX: Make inner white shadow without an extra div */}
          <div ref={optionsRef}>
            {providedGroups ? (
              <div className={styles.groups}>
                {searchedGroups!.map(({ group, options: groupOptions }) => (
                  <div key={group.value} className={styles.group}>
                    <span className={styles.groupLabel}>{group.label}</span>
                    {groupOptions.map((groupOption) => (
                      <button
                        key={groupOption.value}
                        type="button"
                        onClick={() => {
                          onChange?.(groupOption.value!);
                          close();
                        }}
                        className={cx(styles.option, { active: selectedOption?.value === groupOption.value })}
                        data-active={selectedOption?.value === groupOption.value || undefined}
                      >
                        {renderOption(groupOption, { selectedOption, group, search })}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.group}>
                {(searched as IOptions[]).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange?.(option.value!);
                      close();
                    }}
                    className={cx(styles.option, { active: selectedOption })}
                  >
                    {renderOption(option, { selectedOption, search })}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
